import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { Event, Race, normalizeLocation } from '../models/data.models';

export interface EventFetchOptions {
    includeRaces?: boolean;
    includePhotos?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class RaceService {
    private supabase = inject(SupabaseService);

    private readonly eventFields = [
        'id', 'name', 'location', 'date', 'weather', 'description', 'image_url', 'region', 'organizing_club', 'status',
        'is_published', 'city', 'postal_code', 'department_code', 'insee_code', 'latitude',
        'longitude', 'address', 'geo_precision', 'geo_source', 'geo_confidence',
    ].join(',');
    private readonly raceFields = 'id,event_id,name,distance_km,discipline,terrain';
    // description is left out on purpose: prod has that column but databases built from migrations 000 and 007
    // (dev) do not, and asking for a missing column makes PostgREST answer 400. Nothing writes it.
    private readonly photoFields = 'url,title';

    private eventSelection(options: EventFetchOptions = {}): string {
        const includeRaces = options.includeRaces !== false;
        const includePhotos = options.includePhotos === true;
        return [
            this.eventFields,
            includeRaces ? `races(${this.raceFields})` : '',
            includePhotos ? `event_photos(${this.photoFields})` : '',
        ].filter(Boolean).join(',');
    }

    /**
     * Fetch events with pagination
     */
    async getEvents(page: number = 0, pageSize: number = 20): Promise<{ data: Event[], count: number }> {
        const from = page * pageSize;
        const to = from + pageSize - 1;

        // Lists never need the full database rows or the event gallery.
        const { data, error, count } = await this.supabase.client
            .from('events')
            .select(this.eventSelection(), { count: 'exact' })
            .eq('is_published', true)
            .order('date', { ascending: false })
            .range(from, to);

        if (error) {
            console.error('Error fetching events:', error);
            throw error;
        }

        const events: Event[] = (data || []).map(e => this.mapToEvent(e));

        return { data: events, count: count || 0 };
    }

    async getEvent(id: string, publishedOnly = true): Promise<Event | null> {
        let query = this.supabase.client
            .from('events')
            .select(this.eventSelection({ includeRaces: true, includePhotos: true }))
            .eq('id', id);
        if (publishedOnly) query = query.eq('is_published', true);
        const { data, error } = await query.maybeSingle();

        if (error) {
            console.error('Error fetching event:', error);
            return null;
        }

        return data ? this.mapToEvent(data) : null;
    }

    async resolveEventId(id: string, publishedOnly = true): Promise<string | null> {
        let eventQuery = this.supabase.client
            .from('events')
            .select('id')
            .eq('id', id);
        if (publishedOnly) eventQuery = eventQuery.eq('is_published', true);
        const { data: event, error: eventError } = await eventQuery.maybeSingle();
        if (eventError) throw eventError;
        if (event) return event.id;

        let raceQuery = this.supabase.client
            .from('races')
            .select('event_id, event:events!inner(is_published)')
            .eq('id', id);
        if (publishedOnly) raceQuery = raceQuery.eq('event.is_published', true);
        const { data: race, error: raceError } = await raceQuery.maybeSingle();
        if (raceError) throw raceError;
        return race?.event_id ?? null;
    }

    async getPublishedEvents(options: EventFetchOptions = {}): Promise<Event[]> {
        const { data, error } = await this.supabase.client
            .from('events')
            .select(this.eventSelection(options))
            .eq('is_published', true)
            .order('date', { ascending: false })
            .limit(1000);

        if (error) throw error;

        return (data || []).map(e => this.mapToEvent(e));
    }

    /**
     * Fetch all events for administrative and map views.
     */
    async getAllEvents(publishedOnly = false): Promise<Event[]> {
        let query = this.supabase.client
            .from('events')
            .select(this.eventSelection())
            .limit(1000);
        if (publishedOnly) query = query.eq('is_published', true);
        const { data, error } = await query;

        if (error) throw error;

        return (data || []).map(e => this.mapToEvent(e));
    }

    async getAdminEvents(page: number, search = '', status = '', region = ''): Promise<{ events: Event[]; count: number }> {
        let query = this.supabase.client.from('events')
            .select(this.eventSelection({ includeRaces: false }), { count: 'exact' });
        const term = search.trim().replace(/[%_]/g, '');
        if (term) query = query.ilike('name', `%${term}%`);
        if (status) query = query.eq('is_published', status === 'published');
        if (region === 'r3') query = query.in('region', ['Hauts-de-France', 'Région 3']);
        const { data, error, count } = await query.order('date', { ascending: false, nullsFirst: false })
            .order('id').range((page - 1) * 25, page * 25 - 1);
        if (error) throw error;
        return { events: (data || []).map(row => this.mapToEvent(row)), count: count ?? 0 };
    }

    async addEvent(event: Omit<Event, 'id' | 'races'>): Promise<string> {
        const { data, error } = await this.supabase.client
            .from('events')
            .insert({
                name: event.name,
                location: event.location,
                city: event.city,
                postal_code: event.postalCode,
                department_code: event.departmentCode,
                insee_code: event.inseeCode,
                latitude: event.latitude,
                longitude: event.longitude,
                address: event.address,
                geo_precision: event.geoPrecision,
                geo_source: event.geoSource,
                geo_confidence: event.geoConfidence,
                date: event.date,
                weather: event.weather,
                region: event.region,
                description: event.description,
                image_url: event.imageUrl,
                status: event.status || 'upcoming',
                is_published: event.isPublished
            })
            .select('id')
            .single();

        if (error) {
            console.error('Error adding event:', error);
            throw error;
        }
        return data.id;
    }

    getRegionsInZone(zone: string): string[] {
        const zoneMap: { [key: string]: string[] } = {
            'NORD': ['HAUTS-DE-FRANCE', 'NORMANDIE', 'ÎLE-DE-FRANCE'],
            'EST': ['GRAND EST', 'BOURGOGNE-FRANCHE-COMTÉ'],
            'OUEST': ['BRETAGNE', 'PAYS DE LA LOIRE'],
            'SUD': ['OCCITANIE', 'PROVENCE-ALPES-CÔTE D\'AZUR', 'AUVERGNE-RHÔNE-ALPES', 'NOUVELLE-AQUITAINE']
        };
        return zoneMap[zone] || [];
    }

    async deleteEvent(id: string): Promise<void> {
        const { error } = await this.supabase.client
            .from('events')
            .delete()
            .eq('id', id);

        if (error) {
            console.error('Error deleting event:', error);
            throw error;
        }
    }

    private mapToEvent(dbEvent: any): Event {
        return {
            id: dbEvent.id,
            name: dbEvent.name,
            location: normalizeLocation(dbEvent.location),
            date: dbEvent.date,
            weather: dbEvent.weather,
            description: dbEvent.description,
            imageUrl: dbEvent.image_url,
            region: dbEvent.region,
            organizingClub: dbEvent.organizing_club || undefined,
            city: dbEvent.city || undefined,
            postalCode: dbEvent.postal_code || undefined,
            departmentCode: dbEvent.department_code || undefined,
            inseeCode: dbEvent.insee_code || undefined,
            latitude: typeof dbEvent.latitude === 'number' ? dbEvent.latitude : undefined,
            longitude: typeof dbEvent.longitude === 'number' ? dbEvent.longitude : undefined,
            address: dbEvent.address || undefined,
            geoPrecision: dbEvent.geo_precision || undefined,
            geoSource: dbEvent.geo_source || undefined,
            geoConfidence: dbEvent.geo_confidence || undefined,
            status: dbEvent.status,
            isPublished: dbEvent.is_published,
            races: (dbEvent.races || []).map((r: any) => ({
                id: r.id,
                eventId: r.event_id,
                name: r.name,
                distanceKm: r.distance_km,
                terrain: r.terrain,
                discipline: r.discipline
            })),
            eventPhotos: (dbEvent.event_photos || []).map((p: any) => ({
                url: p.url,
                title: p.title,
                description: p.description
            }))
        };
    }
    async updateRace(id: string, updates: Partial<Race>): Promise<void> {
        const dbUpdates: any = {};
        if (updates.name !== undefined) dbUpdates.name = updates.name;
        if (updates.distanceKm !== undefined) dbUpdates.distance_km = updates.distanceKm;
        if (updates.discipline !== undefined) dbUpdates.discipline = updates.discipline;
        if (updates.terrain !== undefined) dbUpdates.terrain = updates.terrain;

        if (Object.keys(dbUpdates).length === 0) return;

        const { error } = await this.supabase.client
            .from('races')
            .update(dbUpdates)
            .eq('id', id);

        if (error) {
            console.error('Error updating race:', error);
            throw error;
        }
    }

    async updateEvent(id: string, updates: Partial<Event>): Promise<void> {
        const dbUpdates: any = {};
        if (updates.name !== undefined) dbUpdates.name = updates.name;
        if (updates.location !== undefined) dbUpdates.location = updates.location;
        if (updates.date !== undefined) dbUpdates.date = updates.date;
        if (updates.weather !== undefined) dbUpdates.weather = updates.weather;
        if (updates.region !== undefined) dbUpdates.region = updates.region;
        if (updates.organizingClub !== undefined) dbUpdates.organizing_club = updates.organizingClub;
        if (updates.city !== undefined) dbUpdates.city = updates.city;
        if (updates.postalCode !== undefined) dbUpdates.postal_code = updates.postalCode;
        if (updates.departmentCode !== undefined) dbUpdates.department_code = updates.departmentCode;
        if (updates.inseeCode !== undefined) dbUpdates.insee_code = updates.inseeCode;
        if (updates.latitude !== undefined) dbUpdates.latitude = updates.latitude;
        if (updates.longitude !== undefined) dbUpdates.longitude = updates.longitude;
        if (updates.address !== undefined) dbUpdates.address = updates.address;
        if (updates.geoPrecision !== undefined) dbUpdates.geo_precision = updates.geoPrecision;
        if (updates.geoSource !== undefined) dbUpdates.geo_source = updates.geoSource;
        if (updates.geoConfidence !== undefined) dbUpdates.geo_confidence = updates.geoConfidence;
        if (updates.description !== undefined) dbUpdates.description = updates.description;
        if (updates.imageUrl !== undefined) dbUpdates.image_url = updates.imageUrl;
        if (updates.status !== undefined) dbUpdates.status = updates.status;
        if (updates.isPublished !== undefined) dbUpdates.is_published = updates.isPublished;

        if (Object.keys(dbUpdates).length === 0) return;

        const { error } = await this.supabase.client
            .from('events')
            .update(dbUpdates)
            .eq('id', id);

        if (error) {
            console.error('Error updating event:', error);
            throw error;
        }
    }
}
